/**
 * Which drill is open on Accounting Dashboards, persisted through the typed
 * /settings/dashboard-layout/{type} endpoints (one user_dashboard_settings row per card the
 * user has ever opened; `visible` marks the open one — at most one, since selection is
 * exclusive like LC Dashboards). These mappers are the whole contract with CardConfigBase;
 * keep them pure.
 *
 * The backend replaces card_config wholesale on PUT (_do_save_layout: `card.config or {}`
 * → `setting.card_config = …`), so anything cardsFromLayoutResponse drops on read is gone
 * after the next save — fine for system cards; extend the mapper before adding per-card
 * settings.
 */
import { ACCT_WORKSPACE_ID } from './accountingPanels';

export interface DrillCardState {
  key: string;
  title: string;
  visible: boolean;
}

export const cardsToLayoutRequest = (cards: DrillCardState[]) => ({
  workspaces: [{ id: ACCT_WORKSPACE_ID, name: 'Main', order: 0, columnCount: 2 }],
  cardConfigs: cards.map((c, i) => ({
    key: c.key,
    title: c.title,
    column: 0,
    order: i,
    visible: c.visible,
    span: 1,
    workspaceId: ACCT_WORKSPACE_ID,
    locked: true,          // system cards: no user-authored filters in this dashboard
    bucket: c.key,
  })),
  columnCount: 2,
  currentWorkspaceId: ACCT_WORKSPACE_ID,
  version: '7',
});

type LayoutResponse = { cardConfigs?: Array<Record<string, unknown>> } | null | undefined;

const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

export const cardsFromLayoutResponse = (resp: LayoutResponse): DrillCardState[] =>
  (resp?.cardConfigs ?? [])
    .filter((c) => typeof c?.key === 'string' && c.key)
    // Saved `order` (= index at save time); a config without one sorts last and keeps its
    // relative position. Sorted before mapping since DrillCardState carries no order; the
    // filter above already copied, so this never mutates the response.
    .sort((a, b) => ((num(a.order) ?? Infinity) - (num(b.order) ?? Infinity)) || 0)
    .map((c) => ({
      key: c.key as string,
      title: typeof c.title === 'string' ? c.title : (c.key as string),
      visible: !!c.visible,
    }));

/**
 * Server cards win; cards the user touched while the load was failing keep their
 * visibility and are appended if the server has never seen them. A no-op on the happy
 * path (local is empty when a first-attempt load succeeds).
 */
export const mergeLoadedCards = (server: DrillCardState[], local: DrillCardState[]): DrillCardState[] => {
  if (local.length === 0) return server;
  const localByKey = new Map(local.map((c) => [c.key, c]));
  const merged = server.map((s) => { const l = localByKey.get(s.key); return l ? { ...s, visible: l.visible } : s; });
  const serverKeys = new Set(server.map((s) => s.key));
  return [...merged, ...local.filter((l) => !serverKeys.has(l.key))];
};

/**
 * Enforce the one-open-drill invariant on a loaded list: keep `preferKey` visible if it is
 * visible, else the first visible card (list order = saved `order`), and hide the rest.
 * Rows from the retired card-grid build may carry several visible cards; the merge path
 * can too (server's open card + the one the user opened during a failed-load window).
 */
export const normalizeExclusive = (cards: DrillCardState[], preferKey?: string | null): DrillCardState[] => {
  const visible = cards.filter((c) => c.visible);
  if (visible.length <= 1) return cards;
  const keep = (preferKey && visible.find((c) => c.key === preferKey)) || visible[0];
  return cards.map((c) => (c.visible && c.key !== keep.key ? { ...c, visible: false } : c));
};
